import React, { useCallback, useEffect, useState } from "react";
import { api, fmtDate, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, ArrowsClockwise, Warning, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import EditRowSheet from "@/components/EditRowSheet";

const UNITS = ["nos", "kg", "mtr", "ltr", "box", "set", "sqft", "cbm"];
const MOVE_TYPES = ["in", "out", "adjust"];

function Field({ label, children }) {
  return <div><Label className="label-caps mb-2 block">{label}</Label>{children}</div>;
}

export default function Inventory({ projectId, embedded = false }) {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [summary, setSummary] = useState([]);
  const [siteFilter, setSiteFilter] = useState(projectId || "all");
  const [open, setOpen] = useState(false);
  const [movingItem, setMovingItem] = useState(null);
  const [historyItem, setHistoryItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [movements, setMovements] = useState([]);

  const emptyItem = { project_id: projectId || "", name: "", sku: "", unit: "nos", category: "", hsn: "", gst_rate: 0, reorder_level: 0, notes: "" };
  const [form, setForm] = useState(emptyItem);

  const emptyMove = { type: "in", qty: 0, date: new Date().toISOString().slice(0, 10), reference: "", notes: "" };
  const [moveForm, setMoveForm] = useState(emptyMove);

  const effectiveFilter = projectId || siteFilter;
  const load = useCallback(() => Promise.all([
    api.get("/inventory", { params: effectiveFilter !== "all" ? { project_id: effectiveFilter } : {} }),
    api.get("/projects"),
    api.get("/inventory/summary/by-site"),
  ]).then(([a, b, c]) => { setRows(a.data); setProjects(b.data); setSummary(c.data); }), [effectiveFilter]);
  useEffect(() => { load(); }, [load]);

  const projMap = Object.fromEntries(projects.map((p) => [p.id, p.name]));

  const submit = async () => {
    if (!form.project_id) return toast.error("Site (project) is required");
    if (!form.name) return toast.error("Item name required");
    try {
      await api.post("/inventory", {
        ...form,
        reorder_level: Number(form.reorder_level || 0),
        gst_rate: Number(form.gst_rate || 0),
      });
      toast.success("Item added");
      setOpen(false); setForm(emptyItem); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this inventory item and all its movements?")) return;
    try { await api.delete(`/inventory/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const openMove = (item) => { setMovingItem(item); setMoveForm(emptyMove); };
  const submitMove = async () => {
    try {
      await api.post(`/inventory/${movingItem.id}/movements`, {
        inventory_id: movingItem.id,
        type: moveForm.type,
        qty: Number(moveForm.qty),
        date: moveForm.date,
        reference: moveForm.reference,
        notes: moveForm.notes,
      });
      toast.success("Stock updated");
      setMovingItem(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const openHistory = async (item) => {
    const { data } = await api.get(`/inventory/${item.id}/movements`);
    setMovements(data);
    setHistoryItem(item);
  };

  return (
    <div data-testid="inventory-page">
      {!embedded && (
      <PageHeader
        title="Inventory (site-wise)"
        subtitle="Track material stock across HVAC project sites."
        actions={
          <div className="flex gap-2">
            <Select value={siteFilter} onValueChange={setSiteFilter}>
              <SelectTrigger className="w-56" data-testid="inventory-site-filter"><SelectValue placeholder="All sites" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sites</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <InventoryAddButton open={open} setOpen={setOpen} form={form} setForm={setForm} projects={projects} projectId={projectId} submit={submit} />
          </div>
        }
      />
      )}
      {embedded && (
        <div className="flex items-center justify-between mb-4">
          <div className="label-caps">Inventory ({rows.length} items)</div>
          <InventoryAddButton open={open} setOpen={setOpen} form={form} setForm={setForm} projects={projects} projectId={projectId} submit={submit} small />
        </div>
      )}

      <div className={embedded ? "space-y-6" : "p-8 space-y-6"}>
        {/* Site summary */}
        {!projectId && summary.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="inventory-summary">
            {summary.map((s) => (
              <button
                key={s.project_id || "none"}
                onClick={() => setSiteFilter(s.project_id || "all")}
                className={`text-left bg-white border p-4 rounded-sm transition-colors ${siteFilter === s.project_id ? "border-[#EA580C]" : "border-slate-200 hover:border-slate-400"}`}
              >
                <div className="label-caps">{s.project_name}</div>
                {s.location && <div className="text-xs text-slate-500 mt-0.5">{s.location}</div>}
                <div className="flex items-baseline gap-2 mt-2">
                  <div className="font-display text-2xl font-bold font-mono-num text-slate-900">{s.item_count}</div>
                  <div className="text-xs text-slate-500">items</div>
                </div>
                <div className="text-xs font-mono-num text-slate-600 mt-1">{s.total_stock_units.toFixed(2)} stock units</div>
              </button>
            ))}
          </div>
        )}

        {rows.length === 0 ? (
          <EmptyState title="No items in inventory" description="Add your first inventory item and start tracking stock in/out." />
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm" data-testid="inventory-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="label-caps">Item</TableHead>
                  <TableHead className="label-caps">SKU</TableHead>
                  <TableHead className="label-caps">HSN</TableHead>
                  <TableHead className="label-caps">Site</TableHead>
                  <TableHead className="label-caps">Category</TableHead>
                  <TableHead className="label-caps text-right">Stock</TableHead>
                  <TableHead className="label-caps text-right">Last Cost</TableHead>
                  <TableHead className="label-caps text-right">GST%</TableHead>
                  <TableHead className="label-caps text-right">Reorder</TableHead>
                  <TableHead className="w-32" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((i) => {
                  const low = i.reorder_level > 0 && Number(i.stock) <= Number(i.reorder_level);
                  const lastCost = Number(i.last_unit_cost || i.unit_cost || 0);
                  return (
                    <TableRow key={i.id} data-testid={`inventory-row-${i.id}`}>
                      <TableCell className="font-medium">
                        {i.name}
                        {low && <Warning size={14} weight="fill" className="inline text-amber-600 ml-2" title="Below reorder level" />}
                      </TableCell>
                      <TableCell className="font-mono-num text-slate-600">{i.sku || "—"}</TableCell>
                      <TableCell className="font-mono text-[11.5px] text-slate-600">{i.hsn || "—"}</TableCell>
                      <TableCell className="text-slate-600">{projMap[i.project_id] || "—"}</TableCell>
                      <TableCell className="text-xs uppercase tracking-wider text-slate-700">{i.category || "—"}</TableCell>
                      <TableCell className={`text-right font-mono-num font-semibold ${low ? "text-amber-700" : "text-slate-900"}`}>{Number(i.stock || 0).toFixed(2)} <span className="text-xs text-slate-500 ml-0.5">{i.unit}</span></TableCell>
                      <TableCell className="text-right font-mono-num text-slate-700 text-[12.5px]">{lastCost > 0 ? `₹${lastCost.toFixed(2)}` : "—"}</TableCell>
                      <TableCell className="text-right font-mono-num text-slate-600 text-[12.5px]">{i.gst_rate > 0 ? `${Number(i.gst_rate)}%` : "—"}</TableCell>
                      <TableCell className="text-right font-mono-num text-slate-500">{Number(i.reorder_level || 0).toFixed(0)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <button onClick={() => openMove(i)} data-testid={`move-inventory-${i.id}`} title="Stock in/out" className="text-emerald-600 hover:text-emerald-800 p-1"><ArrowsClockwise size={14} /></button>
                          <button onClick={() => openHistory(i)} data-testid={`history-inventory-${i.id}`} title="History" className="text-slate-500 hover:text-[#EA580C] p-1 text-xs font-semibold uppercase tracking-widest">Log</button>
                          <button onClick={() => setEditingItem(i)} data-testid={`edit-inventory-${i.id}`} title="Edit" className="text-slate-500 hover:text-[#EA580C] p-1"><PencilSimple size={14} /></button>
                          <button onClick={() => del(i.id)} className="text-slate-400 hover:text-red-600 p-1"><Trash size={14} /></button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Stock movement dialog */}
      <Dialog open={!!movingItem} onOpenChange={(o) => !o && setMovingItem(null)}>
        <DialogContent className="max-w-md" data-testid="movement-dialog">
          {movingItem && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">{movingItem.name}</DialogTitle>
                <div className="text-sm text-slate-500">Current stock: <span className="font-mono-num font-semibold">{Number(movingItem.stock || 0).toFixed(2)} {movingItem.unit}</span></div>
              </DialogHeader>
              <div className="space-y-4">
                <Field label="Movement type">
                  <Select value={moveForm.type} onValueChange={(v) => setMoveForm({ ...moveForm, type: v })}>
                    <SelectTrigger data-testid="movement-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{MOVE_TYPES.map((t) => <SelectItem key={t} value={t}>{t === "in" ? "Stock IN" : t === "out" ? "Stock OUT" : "Adjustment (set)"}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Quantity"><Input type="number" data-testid="movement-qty-input" value={moveForm.qty} onChange={(e) => setMoveForm({ ...moveForm, qty: e.target.value })} /></Field>
                  <Field label="Date"><Input type="date" value={moveForm.date} onChange={(e) => setMoveForm({ ...moveForm, date: e.target.value })} /></Field>
                </div>
                <Field label="Reference"><Input value={moveForm.reference} onChange={(e) => setMoveForm({ ...moveForm, reference: e.target.value })} placeholder="VPO-2001 / Floor 3 install / …" /></Field>
                <Field label="Notes"><Textarea rows={2} value={moveForm.notes} onChange={(e) => setMoveForm({ ...moveForm, notes: e.target.value })} /></Field>
                <Button onClick={submitMove} data-testid="save-movement-button" className="w-full rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white">Record Movement</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit inventory item */}
      {editingItem && (
        <EditRowSheet
          open={!!editingItem}
          onOpenChange={(v) => !v && setEditingItem(null)}
          title="Edit Inventory Item"
          endpoint={`/inventory/${editingItem.id}`}
          initial={editingItem}
          fields={[
            { name: "name", label: "Name", required: true, colSpan: 2 },
            { name: "sku", label: "SKU" },
            {
              name: "unit", label: "Unit", type: "select",
              options: UNITS.map((u) => ({ value: u, label: u })),
            },
            { name: "category", label: "Category" },
            { name: "hsn", label: "HSN / SAC" },
            { name: "gst_rate", label: "GST %", type: "number" },
            { name: "reorder_level", label: "Reorder Level", type: "number" },
            { name: "last_unit_cost", label: "Last Cost (₹)", type: "number" },
            { name: "notes", label: "Notes", type: "textarea", colSpan: 2 },
          ]}
          transformPayload={(f) => ({
            ...f,
            reorder_level: Number(f.reorder_level || 0),
            gst_rate: Number(f.gst_rate || 0),
            last_unit_cost: Number(f.last_unit_cost || 0),
          })}
          onSaved={load}
        />
      )}

      {/* History dialog */}
      <Dialog open={!!historyItem} onOpenChange={(o) => !o && setHistoryItem(null)}>
        <DialogContent className="max-w-2xl" data-testid="history-dialog">
          {historyItem && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">{historyItem.name} — Movement Log</DialogTitle>
              </DialogHeader>
              <div className="max-h-[60vh] overflow-y-auto">
                {movements.length === 0 ? (
                  <div className="text-sm text-slate-400 text-center py-8">No movements yet.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0"><tr>
                      <th className="p-2 text-left label-caps">Date</th>
                      <th className="p-2 text-left label-caps">Type</th>
                      <th className="p-2 text-right label-caps">Qty</th>
                      <th className="p-2 text-right label-caps">After</th>
                      <th className="p-2 text-left label-caps">Reference</th>
                    </tr></thead>
                    <tbody>{movements.map((m) => (
                      <tr key={m.id} className="border-t border-slate-100">
                        <td className="p-2 text-slate-600 font-mono-num">{fmtDate(m.date)}</td>
                        <td className="p-2"><span className={`text-xs uppercase tracking-wider font-semibold ${m.type === "in" ? "text-emerald-700" : m.type === "out" ? "text-red-700" : "text-slate-700"}`}>{m.type}</span></td>
                        <td className="p-2 text-right font-mono-num">{m.type === "out" ? "-" : m.type === "in" ? "+" : "="}{Number(m.qty).toFixed(2)}</td>
                        <td className="p-2 text-right font-mono-num font-semibold">{Number(m.stock_after).toFixed(2)}</td>
                        <td className="p-2 text-slate-600 text-xs">{m.reference || "—"}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InventoryAddButton({ open, setOpen, form, setForm, projects, projectId, submit, small }) {
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button size={small ? "sm" : "default"} data-testid="create-inventory-button" className={`rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white ${small ? "h-8" : ""}`}>
          <Plus size={small ? 14 : 16} className="mr-1" /> Add Item
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[420px]">
        <SheetHeader><SheetTitle className="font-display">Add Inventory Item</SheetTitle></SheetHeader>
        <div className="space-y-4 mt-6">
          {!projectId && (
            <Field label="Site (project)">
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="inventory-site-select"><SelectValue placeholder="Select site" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Item name"><Input data-testid="inventory-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="SKU"><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></Field>
            <Field label="Unit">
              <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category"><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="pipe / duct / valve …" /></Field>
            <Field label="Reorder level"><Input type="number" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="HSN / SAC"><Input value={form.hsn} onChange={(e) => setForm({ ...form, hsn: e.target.value })} placeholder="e.g. 7411" className="font-mono" /></Field>
            <Field label="GST %"><Input type="number" value={form.gst_rate} onChange={(e) => setForm({ ...form, gst_rate: e.target.value })} placeholder="18" /></Field>
          </div>
          <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          <Button onClick={submit} data-testid="save-inventory-button" className="w-full rounded-sm bg-[#0F172A] hover:bg-slate-800">Add Item</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

